import { PlatformLocation } from '@angular/common';
import { Component, Inject, Optional } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { AccessControlType } from '@max/foundation/models/enums/access-control-type.enum';
import { IIncidentModalComponent } from '@max/incident/i-incident-modal.component';
import { Court } from '@max/incident/models/court.model';
import { ACDCode } from '@max/incident/models/enums/acd-code.enum';
import { CourtDispositionCodeType } from '@max/incident/models/enums/court-disposition-code-type.enum';
import { IncidentConvictionModel } from '@max/incident/models/incident-conviction.model';
import { IncidentTypeModel } from '@max/incident/models/incident-type.model';
import { ConvictionIncidentService } from '@max/incident/services/conviction-incident.service';
import { CourtService } from '@max/incident/services/court.service';
import { IncidentTypeService } from '@max/incident/services/incident-type.service';
import { IncidentService } from '@max/incident/services/incident.service';
import { LookupService } from '@max/incident/services/lookup.service';
import { toDateOrNull, toNumberOrNull, toStringOrNull } from '@max/incident/shared/data-helpers';
import { IncidentDialogContext } from '@max/incident/shared/incident-dialog-context.model';
import { BaseComponent } from '@max/shared/base-components/base.component';
import { MvdModalService } from '@max/shared/services/modal/mvd-modal.service';
import * as moment from 'moment';
import { forkJoin, Observable } from 'rxjs';
import { filter, finalize, flatMap, tap } from 'rxjs/operators';

@Component({
    selector: 'conviction-incident-modal',
    templateUrl: './conviction-incident-modal.html'
})
export class AzConvictionIncidentModalComponent extends BaseComponent implements IIncidentModalComponent<IncidentConvictionModel> {
    public busy = false;
    public yearRange: string;
    public incidentReferenceTypeDisplayValue: string;
    public form: UntypedFormGroup;
    public acdDisplay: string;
    public incidentTypeDisplayValue: string;
    public lastReevaluatedBy: string;
    public lastReevaluatedDate: Date;
    public violationCode: string;
    public acdCodeDisplayValue: string;
    public incidentTypeDescription: string;
    public selectedCourt: Court;
    public showIncidentStatusReasonType = false;
    public courts: Court[] = [];
    public incidentStatusReasonTypes: any[] = [];
    public incidentStatusTypes: any[] = [];
    public jurisdictions: any[] = [];
    public plateTypes: any[] = [];
    public citingAgencyTypes: any[] = [];
    public offenseClassificationTypes: any[] = [];
    public accidentTypes: any[] = [];
    public courtDispositionCodeTypes: any[] = [];
    public drugViolationTypeTypes: any[] = [];
    public convictionViolationCodes: any[] = [];
    public courtOrderedInterlockMonths: any[] = [];
    public courtActionCodeTypes: any[] = [];
    public courtOrderedActionTypes: any[] = [];
    public sentencingAuthorities: any[] = [];
    public speedRequired = false;
    public underlyingViolationCodeRequired = false;
    public canSkipLetterGeneration = false;
    public canGetLatest = true;
    private allConvictionViolationCodes: any[] = [];
    private convictionCriminalViolationCodes: any[] = [];
    private derivedCdl: boolean = null;
    private canEditSeventyEights = false;
    private originalDispositionId: number;
    private csrCdlOverride = false;
    private disableCdlValidation = false;
    private incidentType: IncidentTypeModel;
    private courtDispositionCodeTypeEnum: CourtDispositionCodeType = new CourtDispositionCodeType();
    private acdCodes: ACDCode = new ACDCode();

    constructor(
        @Optional() @Inject(MAT_DIALOG_DATA)
        public data: IncidentDialogContext<IncidentConvictionModel>,
        public dialog: MatDialogRef<AzConvictionIncidentModalComponent>,
        private lookupService: LookupService,
        private incidentTypeService: IncidentTypeService,
        private incidentConvictionService: ConvictionIncidentService,
        private incidentService: IncidentService,
        private modalService: MvdModalService,
        private courtService: CourtService,
        location: PlatformLocation
    ) {
        super();
        this.yearRange = `1901:${moment().year() + 30}`;
        location.onPopState(() => dialog.close(null));
    }

    public ngOnInit(): void {
        let accessControlTypes = new AccessControlType();
        this.canSkipLetterGeneration = this.auth.hasACSTransactionClaimEx(this.auth.acsTransactionClaim, accessControlTypes.AllowPrintLetterOption.value);
        this.canEditSeventyEights = this.auth.hasACSTransactionClaimEx(this.auth.acsTransactionClaim, accessControlTypes.EditDispositionCode78.value) == true;
    }

    public initLookups = (lookups: any): Observable<any> => forkJoin({
        lookups: this.lookupService.getLookups(),
        incidentType: this.incidentTypeService.getIncidentType(this.data.incident.incidentTypeId),
        courts: this.courtService.getAll(),
        cdlStatus: this.incidentService.getCDLStatusOnDate(this.data.incident)
    }).pipe(tap(r => this.loadLookups(r.lookups, r.courts, r.incidentType, r.cdlStatus)));

    public closeModal = (result?: any): void => {
        this.dialog.close(result);
    };

    public populateModel = (form: UntypedFormGroup, model: IncidentConvictionModel): void => {
        model.driverLicenseNumber = form.get('driverLicenseNumber').value;
        model.driverLicenseJurisdictionId = toNumberOrNull(form.get('driverLicenseJurisdictionId').value);
        model.vehicleLicensePlateNumber = form.get('vehicleLicensePlateNumber').value;
        model.plateTypeId = toNumberOrNull(form.get('plateTypeId').value);
        model.vehicleRegistrationJurisdictionId = toNumberOrNull(form.get('vehicleRegistrationJurisdictionId').value);
        model.vehicleMake = form.get('vehicleMake').value;
        model.courtId = toNumberOrNull(form.get('courtId').value);
        model.citingAgencyTypeId = toNumberOrNull(form.get('citingAgencyTypeId').value);
        model.approximateSpeed = form.get('approximateSpeed').value;
        model.lawfulSpeed = form.get('lawfulSpeed').value;
        model.offenseClassificationTypeId = toNumberOrNull(form.get('offenseClassificationTypeId').value);
        model.accidentTypeId = toNumberOrNull(form.get('accidentTypeId').value);
        model.cdlHolderIndicator = true == form.get('isCDLHolder').value;
        model.csrCdlOverride = this.csrCdlOverride;
        model.complaintNumber = form.get('complaintNumber').value;
        model.complaintSequenceNumber = form.get('complaintSequenceNumber').value;
        model.dispositionDate = toDateOrNull(form.get('dispositionDate').value);
        model.courtDispositionCodeTypeId = toNumberOrNull(form.get('courtDispositionCodeTypeId').value);
        model.drugViolationTypeId = toNumberOrNull(form.get('drugViolationTypeId').value);
        model.commercialMotorVehicleIndicator = true == form.get('commercialMotorVehicleIndicator').value;
        model.hazardousMaterialIndicator = true == form.get('hazardousMaterialIndicator').value;
        model.passengerIndicator = true == form.get('passengerIndicator').value;
        model.operatingHireFlag = true == form.get('operatingHireFlag').value;
        model.alternativeServiceProcessIndicator = true == form.get('alternativeServiceProcessIndicator').value;
        model.underlyingViolationCode = form.get('underlyingViolationCode').value;
        model.courtOrderedInterlockMonthsTypeId = toNumberOrNull(form.get('courtOrderedInterlockMonthsTypeId').value);
        model.courtActionCodeTypeId = toNumberOrNull(form.get('courtActionCodeTypeId').value);
        model.courtOrderedActionTypeId = toNumberOrNull(form.get('courtOrderedActionTypeId').value);
        model.convictionSentencingAuthorityTypeId = toNumberOrNull(form.get('sentencingAuthorityNameId').value);
        model.alcoholClassIndicator = true == form.get('alcoholClassReqCheck').value;
        model.withdrawalBeginningActionDate = toDateOrNull(form.get('withdrawalBeginningActionDate').value);
        model.withdrawalEndingActionDate = toDateOrNull(form.get('withdrawalEndingActionDate').value);
        model.withdrawalNumberOfMonths = toNumberOrNull(form.get('withdrawalNumberOfMonths').value);
        model.restrictionBeginningActionDate = toDateOrNull(form.get('restrictionBeginningActionDate').value);
        model.restrictionEndingActionDate = toDateOrNull(form.get('restrictionEndingActionDate').value);
    };

    public formInitialized = (form: UntypedFormGroup): void => {
        this.form = form;
        form.addControl('driverLicenseNumber', new UntypedFormControl(''));
        form.addControl('driverLicenseJurisdictionId', new UntypedFormControl(null));
        form.addControl('vehicleLicensePlateNumber', new UntypedFormControl(''));
        form.addControl('plateTypeId', new UntypedFormControl(null));
        form.addControl('vehicleRegistrationJurisdictionId', new UntypedFormControl(null));
        form.addControl('vehicleMake', new UntypedFormControl(''));
        form.addControl('courtId', new UntypedFormControl(null, Validators.required));
        form.addControl('citingAgencyTypeId', new UntypedFormControl(null, Validators.required));
        form.addControl('approximateSpeed', new UntypedFormControl(''));
        form.addControl('lawfulSpeed', new UntypedFormControl(''));
        form.addControl('offenseClassificationTypeId', new UntypedFormControl(null, Validators.required));
        form.addControl('accidentTypeId', new UntypedFormControl(null, Validators.required));
        form.addControl('isCDLHolder', new UntypedFormControl(false));
        form.addControl('complaintNumber', new UntypedFormControl('', Validators.required));
        form.addControl('complaintSequenceNumber', new UntypedFormControl('', Validators.required));
        form.addControl('dispositionDate', new UntypedFormControl('', Validators.required));
        form.addControl('courtDispositionCodeTypeId', new UntypedFormControl(null, Validators.required));
        form.addControl('drugViolationTypeId', new UntypedFormControl(null, Validators.required));
        form.addControl('commercialMotorVehicleIndicator', new UntypedFormControl(false));
        form.addControl('hazardousMaterialIndicator', new UntypedFormControl(false));
        form.addControl('passengerIndicator', new UntypedFormControl(false));
        form.addControl('operatingHireFlag', new UntypedFormControl(false));
        form.addControl('alternativeServiceProcessIndicator', new UntypedFormControl(false));
        form.addControl('underlyingViolationCode', new UntypedFormControl(null));
        form.addControl('courtOrderedInterlockMonthsTypeId', new UntypedFormControl(null));
        form.addControl('courtActionCodeTypeId', new UntypedFormControl(null));
        form.addControl('courtOrderedActionTypeId', new UntypedFormControl(null));
        form.addControl('sentencingAuthorityNameId', new UntypedFormControl(null));
        form.addControl('alcoholClassReqCheck', new UntypedFormControl(false));
        form.addControl('withdrawalBeginningActionDate', new UntypedFormControl(null));
        form.addControl('withdrawalEndingActionDate', new UntypedFormControl(null));
        form.addControl('withdrawalNumberOfMonths', new UntypedFormControl(''));
        form.addControl('restrictionBeginningActionDate', new UntypedFormControl(null));
        form.addControl('restrictionEndingActionDate', new UntypedFormControl(null));
        form.addControl('convictionDate', new UntypedFormControl(null));
        form.get('courtId').valueChanges.subscribe(v => this.courtChanged(v));
        form.get('isCDLHolder').valueChanges.subscribe(v => this.isCdlHolderChanged(v));
        form.get('courtDispositionCodeTypeId').valueChanges.subscribe(v => this.courtDispositionCodeChanged(v));
        form.get('underlyingViolationCode').valueChanges.subscribe(v => this.underlyingViolationCodeChanged(v));
        form.get('dispositionDate').valueChanges.subscribe(v => this.dispositionDateChanged(v));
        form.get('occurrenceDate').valueChanges.subscribe(v => this.occurrenceDateChanged(v));
        form.get('convictionDate').disable();
    };

    public updateFormValidationRules = (form: UntypedFormGroup, incidentTypeId: number): void => {
        const speeding701AIncidentTypeId = 324;
        const failureToAppearIncidentTypeId = 321;
        const failureToPayIncidentTypeId = 322;
        const failureToPayCriminalIncidentTypeId = 755;
        const failureToAppearCriminalIncidentTypeId = 758;
        this.speedRequired = false;
        this.underlyingViolationCodeRequired = false;
        let approximateSpeedControl = form.get('approximateSpeed');
        let lawfulSpeedControl = form.get('lawfulSpeed');

        if ((null != lawfulSpeedControl) &&
            (null != approximateSpeedControl) &&
            (speeding701AIncidentTypeId == incidentTypeId)
        ) {
            this.speedRequired = true;
            approximateSpeedControl.setValidators(Validators.required);
            lawfulSpeedControl.setValidators(Validators.required);
        }

        let underlyingViolationCodeControl = form.get('underlyingViolationCode');

        if ((null != underlyingViolationCodeControl) &&
            ((failureToAppearIncidentTypeId == incidentTypeId) ||
             (failureToPayIncidentTypeId == incidentTypeId) ||
             (failureToPayCriminalIncidentTypeId == incidentTypeId) ||
             (failureToAppearCriminalIncidentTypeId == incidentTypeId))
        ) {
            this.underlyingViolationCodeRequired = true;
            underlyingViolationCodeControl.setValidators(Validators.required);
        }
    };

    public updateData = (form: UntypedFormGroup, incident: IncidentConvictionModel): void => {
        this.originalDispositionId = toNumberOrNull(incident.courtDispositionCodeTypeId);
        form.get('driverLicenseNumber').setValue(incident.driverLicenseNumber);
        form.get('driverLicenseJurisdictionId').setValue(toNumberOrNull(incident.driverLicenseJurisdictionId));
        form.get('vehicleLicensePlateNumber').setValue(incident.vehicleLicensePlateNumber);
        form.get('plateTypeId').setValue(toNumberOrNull(incident.plateTypeId));
        form.get('vehicleRegistrationJurisdictionId').setValue(toNumberOrNull(incident.vehicleRegistrationJurisdictionId));
        form.get('vehicleMake').setValue(incident.vehicleMake);
        form.get('courtId').setValue(toNumberOrNull(incident.courtId));
        form.get('citingAgencyTypeId').setValue(toNumberOrNull(incident.citingAgencyTypeId));
        form.get('approximateSpeed').setValue(incident.approximateSpeed);
        form.get('lawfulSpeed').setValue(incident.lawfulSpeed);
        form.get('accidentTypeId').setValue(toNumberOrNull(incident.accidentTypeId));
        form.get('complaintNumber').setValue(incident.complaintNumber);
        form.get('complaintSequenceNumber').setValue(incident.complaintSequenceNumber);
        form.get('dispositionDate').setValue(incident.dispositionDate);
        form.get('courtDispositionCodeTypeId').setValue(toNumberOrNull(incident.courtDispositionCodeTypeId));
        form.get('drugViolationTypeId').setValue(toNumberOrNull(incident.drugViolationTypeId));
        form.get('commercialMotorVehicleIndicator').setValue(true == incident.commercialMotorVehicleIndicator);
        form.get('hazardousMaterialIndicator').setValue(true == incident.hazardousMaterialIndicator);
        form.get('passengerIndicator').setValue(true == incident.passengerIndicator);
        form.get('operatingHireFlag').setValue(true == incident.operatingHireFlag);
        form.get('alternativeServiceProcessIndicator').setValue(true == incident.alternativeServiceProcessIndicator);
        form.get('courtOrderedInterlockMonthsTypeId').setValue(toNumberOrNull(incident.courtOrderedInterlockMonthsTypeId));
        form.get('courtActionCodeTypeId').setValue(toNumberOrNull(incident.courtActionCodeTypeId));
        form.get('courtOrderedActionTypeId').setValue(toNumberOrNull(incident.courtOrderedActionTypeId));
        form.get('sentencingAuthorityNameId').setValue(toNumberOrNull(incident.convictionSentencingAuthorityTypeId));
        form.get('alcoholClassReqCheck').setValue(true == incident.alcoholClassIndicator);
        form.get('withdrawalBeginningActionDate').setValue(incident.withdrawalBeginningActionDate);
        form.get('withdrawalEndingActionDate').setValue(incident.withdrawalEndingActionDate);
        form.get('withdrawalNumberOfMonths').setValue(incident.withdrawalNumberOfMonths);
        form.get('restrictionBeginningActionDate').setValue(incident.restrictionBeginningActionDate);
        form.get('restrictionEndingActionDate').setValue(incident.restrictionEndingActionDate);
        form.get('offenseClassificationTypeId').setValue(toNumberOrNull(incident.offenseClassificationTypeId));
        form.get('convictionDate').setValue(incident.convictionDate);

        // Avoiding emitting of event so that delegate function will not overwrite the retrieved ACDCode from the database
        form.get('underlyingViolationCode').setValue(toStringOrNull(incident.underlyingViolationCode), { emitEvent: false });
        if (!!incident.underlyingAcdCodeId) {
            this.setAcdData(incident.underlyingAcdCodeId);
        }

        if (null != toNumberOrNull(incident.incidentNumberId)) {
            this.setCdlHolderValue(true == incident.cdlHolderIndicator);
        }
    };

    public getIncident = (incidentNumberId: number): Observable<IncidentConvictionModel> => this.incidentConvictionService.getIncident(incidentNumberId);

    public save = (model: IncidentConvictionModel): Observable<any> => this.incidentConvictionService.saveIncident(model);

    public loadComplete = (): void => {
        if (null == toNumberOrNull(this.data.incident.incidentNumberId)) {
            this.form.get('offenseClassificationTypeId').setValue(toNumberOrNull(this.incidentType.offenseClassificationTypeId));
        }
    };

    public getLatestIncident = (): void => {
        this.canGetLatest = false;
        this.incidentConvictionService.getCustomersLastConviction(this.data.customerNumberId)
            .subscribe(r => {
                if (null != r.id) {
                    this.form.get('occurrenceDate').setValue(r.occurrenceDate);
                    this.form.get('receivedDate').setValue(r.receivedDate);
                    this.form.get('referenceNumber').setValue(r.referenceNumber);
                    this.form.get('vehicleLicensePlateNumber').setValue(r.vehicleLicensePlateNumber);

                    if (!!r.underlyingAcdCodeId) {
                        this.setAcdData(r.underlyingAcdCodeId);
                    }

                    if (r.plateTypeId > 0) {
                        this.form.get('plateTypeId').setValue(r.plateTypeId);
                    }

                    if (r.vehicleRegistrationJurisdictionId > 0) {
                        this.form.get('vehicleRegistrationJurisdictionId').setValue(r.vehicleRegistrationJurisdictionId);
                    }

                    this.form.get('vehicleMake').setValue(r.vehicleMake);
                    this.form.get('courtId').setValue(r.courtId);

                    if (r.citingAgencyTypeId > 0) {
                        this.form.get('citingAgencyTypeId').setValue(r.citingAgencyTypeId);
                    }

                    if (r.courtId > 0) {
                        this.form.get('courtId').setValue(r.courtId);
                    }
                } else {
                    this.modalService.alert()
                        .title('Warning')
                        .body('The customer does not have a previous conviction to copy.')
                        .okBtn('OK')
                        .open();
                }
            },
            e => this.handleValidationResult(e));
    };

    private loadLookups = (
        lookups: any,
        courts: { result: Court[] },
        incidentType: IncidentTypeModel,
        cdlStatus: { result: boolean },
    ): void => {
        this.courts = courts.result || [];
        this.incidentStatusReasonTypes = lookups.result.incidentStatusReasonTypes || [];
        this.incidentStatusTypes = lookups.result.incidentStatusTypes || [];
        this.jurisdictions = lookups.result.vehicleJurisdictions || [];
        this.plateTypes = lookups.result.plateTypes || [];
        this.citingAgencyTypes = lookups.result.citingAgencyTypes || [];
        this.offenseClassificationTypes = lookups.result.offenseClassificationTypes || [];
        this.accidentTypes = lookups.result.accidentTypes || [];
        this.drugViolationTypeTypes = lookups.result.drugViolationTypeTypes || [];
        this.allConvictionViolationCodes = lookups.result.convictionViolationCodes || [];
        this.convictionViolationCodes = this.allConvictionViolationCodes;
        this.convictionCriminalViolationCodes = lookups.result.convictionCriminalViolationCodes || [];
        this.courtOrderedInterlockMonths = lookups.result.courtOrderedInterlockMonthsTypes || [];
        this.courtActionCodeTypes = lookups.result.courtActionCodeTypes || [];
        this.courtOrderedActionTypes = lookups.result.courtOrderedActionTypes || [];
        this.sentencingAuthorities = lookups.result.sentencingAgencyTypes || [];
        this.derivedCdl = true == cdlStatus.result;
        this.incidentType = incidentType;

        // We are "converting" value to a number from a string because the value is bound to a number rather than a string.
        // Having this.courtDispositionCodeTypes's value be set to strings breaks binding with the dropdown for these types
        if (lookups.result.courtDispositionCodeTypes) {
            lookups.result.courtDispositionCodeTypes.map(t => t.value = toNumberOrNull(t.value));
        }

        this.courtDispositionCodeTypes = lookups.result.courtDispositionCodeTypes || [];

        this.setCdlHolderValue(this.derivedCdl);
    };

    private courtChanged = (courtId: number): void => {
        if (null != this.courts) {
            this.selectedCourt = this.courts.find(e => e.id == courtId);
        }
    };

    private setCdlHolderValue = (value: boolean): void => {
        this.disableCdlValidation = true;

        try {
            this.form.get('isCDLHolder').setValue(value);
        } finally {
            this.disableCdlValidation = false;
        }
    };

    private isCdlHolderChanged = (value: boolean): void => {
        if (true == this.disableCdlValidation) {
            return;
        }

        this.csrCdlOverride = false;

        if ((null != this.derivedCdl) && (value != this.derivedCdl)) {
            this.modalService.confirm()
                .title('CDL Holder Confirmation')
                .body('MAX has derived a different value for CDL Holder indicator than what you have selected, do you want to continue?')
                .okBtn('Yes')
                .cancelBtn('No')
                .open()
                .afterClosed()
                .subscribe(r => {
                    if (false == r) {
                        this.setCdlHolderValue(true == this.derivedCdl);
                    } else {
                        this.csrCdlOverride = true;
                    }
                });
        }
    };

    private courtDispositionCodeChanged = (courtDispositionCodeTypeId: number): void => {
        if (null == this.data.incident.legislativeChangeDates) {
            return;
        }

        for (let d of this.data.incident.legislativeChangeDates) {
            if ('Legislation2021' == d.legislativeChangeDateTypeValue) {
                if ((13 == courtDispositionCodeTypeId) ||
                    (14 == courtDispositionCodeTypeId) ||
                    (23 == courtDispositionCodeTypeId) ||
                    (24 == courtDispositionCodeTypeId)
                ) {
                    this.convictionViolationCodes = this.convictionCriminalViolationCodes;
                } else {
                    this.convictionViolationCodes = this.allConvictionViolationCodes;
                }
            }
        }
    };

    private underlyingViolationCodeChanged = (violationCode: string): void => {
        this.acdDisplay = '';

        if ((null == violationCode) || ('' == violationCode)) {
            return;
        }

        var occurDate = moment(this.form.get('occurrenceDate').value).format('MM-DD-YYYY');
        this.incidentTypeService.getIncidentTypeByViolationCodes(violationCode, occurDate)
            .pipe(filter(r => (null != r) && (null != r.acdCodeId)))
            .subscribe(r => {
                if (!!r && !!r.acdCodeId) {
                    this.setAcdData(r.acdCodeId);
                }
            });
    };

    private dispositionDateChanged = (dispositionDate: Date): void => {
        var dispDate = moment(dispositionDate).format('MM-DD-YYYY');
        let incidentNumberId = toNumberOrNull(this.data.incident.incidentNumberId);

        if (null == incidentNumberId) {
            this.incidentConvictionService.getCourtDispositionCodeTypeByEffectiveDate(dispDate)
                .pipe(flatMap(r => {
                    if (r.result) {
                        this.courtDispositionCodeTypes = r.result.map(e => <any> {
                            text: e.courtDispositionCodeTypeDisplayValue,
                            value: toNumberOrNull(e.id)
                        });

                        if (this.data.incident.incidentNumberId == 0) {
                            if (this.data.incident.incidentTypeId == 321) {
                                this.courtDispositionCodeTypes = this.courtDispositionCodeTypes.filter(x => x.value == 13 || x.value == 14 || x.value == 15);
                            } else if (this.data.incident.incidentTypeId == 758) {
                                this.courtDispositionCodeTypes = this.courtDispositionCodeTypes.filter(x => x.value == 13 || x.value == 14);
                            } else if (this.data.incident.incidentTypeId == 322) {
                                this.courtDispositionCodeTypes = this.courtDispositionCodeTypes.filter(x => x.value == 23 || x.value == 24 || x.value == 12);
                            } else if (this.data.incident.incidentTypeId == 755) {
                                this.courtDispositionCodeTypes = this.courtDispositionCodeTypes.filter(x => x.value == 23 || x.value == 24);
                            } else {
                                this.courtDispositionCodeTypes = this.courtDispositionCodeTypes.filter(x => x.value != 13 && x.value != 14 && x.value != 15 && x.value != 23 && x.value != 24 && x.value != 12);
                            }

                            if (this.incidentType.civilCriminalViolationTypeId == 2) {
                                this.courtDispositionCodeTypes = this.courtDispositionCodeTypes.filter(x => x.value != 33);
                            }
                        }
                    }

                    if (((this.data.incident.violationCode == '8-323') ||
                         (this.data.incident.violationCode == '13-3405A4') ||
                         (this.data.incident.violationCode == '13-3408A7')) &&
                        this.canEditSeventyEights) {
                        // Leave 78
                    } else {
                        this.courtDispositionCodeTypes = this.courtDispositionCodeTypes.filter(x => x.value != 32);
                    }

                    return this.incidentConvictionService.getLegislativeChangeDateByEffectiveDate(dispDate);
                })).subscribe(r => {
                    if (r.result) {
                        this.data.incident.legislativeChangeDates = r.result;
                    }
                });
        } else {
            this.incidentConvictionService.GetCourtDispositionCodeTypeByEffectiveDateAndDispositionTypeId(dispDate, this.originalDispositionId)
                .pipe(flatMap(r => {
                    if (r.result) {
                        this.courtDispositionCodeTypes = r.result.map(e => <any> {
                            text: e.courtDispositionCodeTypeDisplayValue,
                            value: toNumberOrNull(e.id)
                        });

                        if (this.incidentType && this.incidentType.civilCriminalViolationTypeId == 2) {
                            this.courtDispositionCodeTypes = this.courtDispositionCodeTypes.filter(x => x.value != 33);
                        }
                    }

                    if (((this.data.incident.violationCode == '8-323') ||
                         (this.data.incident.violationCode == '13-3405A4') ||
                        (this.data.incident.violationCode == '13-3408A7')) &&
                        this.canEditSeventyEights) {
                        // Leave 78
                    } else {
                        this.courtDispositionCodeTypes = this.courtDispositionCodeTypes.filter(x => x.value != 32);
                    }

                    return this.incidentConvictionService.getLegislativeChangeDateByEffectiveDate(dispDate);
                })).subscribe(r => {
                    if (r.result) {
                        this.data.incident.legislativeChangeDates = r.result;
                    }
                });
        }
    };

    private occurrenceDateChanged = (occurrenceDate): void => {
        if (null == toNumberOrNull(this.data.incident.incidentNumberId) && !this.busy) {
            this.data.incident.occurrenceDate = toDateOrNull(occurrenceDate);

            if (null != this.data.incident.occurrenceDate) {
                this.busy = true;
                this.incidentService.getCDLStatusOnDate(this.data.incident)
                    .pipe(finalize(() => this.busy = false))
                    .subscribe(r => {
                        this.setCdlHolderValue(true == r.result);
                        this.derivedCdl = true == r.result;
                    },
                    e => this.handleValidationResult(e));
            }
        }
    };

    private setAcdData(acdCodeId: number) {
        let acd = this.acdCodes.FromId(acdCodeId);
        this.acdDisplay = `${acd.value} - ${acd.description}`;
        this.data.incident.underlyingAcdCodeId = acd.id;
    }
}
